import React, { useState, useEffect } from 'react';
import { Clock, Sun, CloudSun, Moon, Check, Bell } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIME_OPTIONS = [
  { id: 'morning', label: 'Morning', desc: '9:00 AM', icon: Sun, color: 'text-amber-500 bg-amber-50 border-amber-200' },
  { id: 'afternoon', label: 'Afternoon', desc: '1:00 PM', icon: CloudSun, color: 'text-blue-500 bg-blue-50 border-blue-200' },
  { id: 'evening', label: 'Evening', desc: '7:00 PM', icon: Moon, color: 'text-indigo-500 bg-indigo-50 border-indigo-200' },
];

/**
 * SessionReminderCard — Users pick preferred practice time, see countdown.
 */
const SessionReminderCard = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
    if (!token) return;
    fetch(`${API_URL}/api/engagement/v2/session-reminder`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(setData).catch(() => {});
  }, []);

  const setPreference = async (time) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
      await fetch(`${API_URL}/api/engagement/v2/session-reminder`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferred_time: time, enabled: true }),
      });
      setData(prev => ({ ...prev, preferred_time: time }));
    } catch {}
    finally { setSaving(false); }
  };

  if (!data) return null;

  const hoursUntil = data.hours_until_session || 0;
  const minutesDisplay = hoursUntil > 0 ? `${hoursUntil}h` : 'Now';

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid="session-reminder-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-orange-500" />
          <span className="text-sm font-bold text-gray-800">Practice Reminder</span>
        </div>
        {data.practised_today && (
          <span className="flex items-center gap-1 text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
            <Check size={10} /> Done today
          </span>
        )}
      </div>

      {/* Countdown */}
      {!data.practised_today && (
        <div className="flex items-center gap-2 mb-3 p-2 bg-orange-50 rounded-lg">
          <Bell size={14} className="text-orange-500" />
          <span className="text-xs text-orange-700 font-medium">
            {data.nudge ? "Time to practise! Your session window is open." : `Next session in ${minutesDisplay}`}
          </span>
        </div>
      )}

      {/* Time preference selector */}
      <div className="grid grid-cols-3 gap-2">
        {TIME_OPTIONS.map(opt => {
          const Icon = opt.icon;
          const selected = data.preferred_time === opt.id;
          return (
            <button
              key={opt.id}
              onClick={() => setPreference(opt.id)}
              disabled={saving}
              className={`p-2 rounded-lg border text-center transition-all ${
                selected ? opt.color + ' border-2 shadow-sm' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
              data-testid={`reminder-${opt.id}`}
            >
              <Icon size={16} className={`mx-auto mb-1 ${selected ? '' : 'text-gray-400'}`} />
              <p className="text-[10px] font-bold text-gray-700">{opt.label}</p>
              <p className="text-[10px] text-gray-400">{opt.desc}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SessionReminderCard;
